import { type QueryKey } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectsQuery } from '@/features/projects/hooks/projects';
import { useProjectMetricThresholds } from '@/features/projects/hooks/useProjectMetricThresholds';
import { useProjectStatusPresentation } from '@/features/shared/hooks/useProjectStatusPresentation';
import { buildTagColorByName, mergeTagColorByName } from '@/features/shared/tags';
import { suitesGridConfig } from '@/features/suites/grid/gridConfig';
import { useTagOptions } from '@/shared/hooks';
import { buildSuitesColumns } from './suitesColumns';
import { useSuitesTableEditDialog } from './useSuitesTableEditDialog';
import { useSuitesTableMetricsModel } from './useSuitesTableMetricsModel';
import { useSuitesTableRowActions } from './useSuitesTableRowActions';
import type { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import type {
  SuiteEditValues,
  SuitesTableProps,
} from '@/features/suites/components/SuitesTable.types';
import type { TagColor } from '@/shared/data/tags';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import type { useFormErrors } from '@/ui/hooks/useFormErrors';
import type { GridColDef } from '@/ui/primitives';
import type { MetricThresholdGroup, QueryResult, SuiteListItemDto } from '@flowqa/shared';

type SuitesTableControllerInput = Pick<
  SuitesTableProps,
  | 'disableLinks'
  | 'disableSummaryLinks'
  | 'enableMetricsBatch'
  | 'fixedFilters'
  | 'hideActionsColumn'
  | 'initialFilterModel'
  | 'initialSortModel'
  | 'isAuthenticatedOverride'
  | 'metricSeriesBySuiteIdOverride'
  | 'metricsBatchDelayMs'
  | 'metricsEndDay'
  | 'metricsStartDay'
  | 'onRowsChange'
  | 'onStatusSelect'
  | 'persistKey'
  | 'queryFn'
  | 'queryKey'
  | 'showDeltas'
  | 'sparklineRangeDays'
  | 'syncStateToUrl'
  | 'tagColorByNameOverride'
  | 'tagOptionsOverride'
>;

type SuitesTableController = {
  columns: Array<GridColDef<SuiteListItemDto>>;
  deleteLoading: boolean;
  deleteTarget: ReturnType<typeof useSuitesTableRowActions>['deleteTarget'];
  editEditor: ReturnType<typeof useDetailEditEditor<string>>;
  editErrors: ReturnType<typeof useFormErrors<SuiteEditValues>>;
  editForm: ReturnType<typeof useDialogFormState<SuiteEditValues>>;
  editTarget: SuiteListItemDto | null;
  gridConfig: ReturnType<typeof suitesGridConfig>;
  handleEditClose: () => void;
  handleEditSave: (input: { tags: string[]; colors: TagColor[] }) => Promise<void>;
  handleRowsChange: (rows: SuiteListItemDto[]) => void;
  isAuthenticated: boolean;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  queryFn: (input: GridQueryInput) => Promise<QueryResult<SuiteListItemDto>>;
  queryKey: QueryKey;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  togglableColumnFields: string[];
};

export const useSuitesTableController = ({
  disableLinks = false,
  disableSummaryLinks = false,
  enableMetricsBatch = true,
  fixedFilters,
  hideActionsColumn = false,
  initialFilterModel,
  initialSortModel,
  isAuthenticatedOverride,
  metricSeriesBySuiteIdOverride,
  metricsBatchDelayMs = 0,
  metricsEndDay,
  metricsStartDay,
  onRowsChange,
  onStatusSelect,
  persistKey,
  queryFn,
  queryKey,
  showDeltas = true,
  sparklineRangeDays = 7,
  syncStateToUrl = false,
  tagColorByNameOverride,
  tagOptionsOverride,
}: SuitesTableControllerInput): SuitesTableController => {
  const { session } = useAuth();
  const isAuthenticated = isAuthenticatedOverride ?? session !== null;
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId, setSelectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const {
    groupErroredWithFailed,
    preferFileNameOverDisplayName,
    resolveGroupErroredWithFailed,
    resolvePreferFileNameOverDisplayName,
  } = useProjectStatusPresentation();
  const { tagOptions: fetchedTagOptions } = useTagOptions({
    projectId: selectedProjectId,
    enabled: tagOptionsOverride === undefined,
  });
  const tagOptions = tagOptionsOverride ?? fetchedTagOptions;
  const [visibleRows, setVisibleRows] = useState<SuiteListItemDto[]>([]);
  const {
    editEditor,
    editErrors,
    editForm,
    editTarget,
    handleEditClose,
    handleEditRow,
    handleEditSave,
  } = useSuitesTableEditDialog();
  const tagColorByName = useMemo(() => buildTagColorByName(tagOptions), [tagOptions]);
  const resolvedTagColorByName = useMemo(
    () => mergeTagColorByName(tagColorByName, tagColorByNameOverride),
    [tagColorByName, tagColorByNameOverride],
  );
  const {
    deleteTarget,
    deleteLoading,
    deleteRowPendingId,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
  } = useSuitesTableRowActions({ selectedProjectId });
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.suites]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const { metricSeriesBySuiteId } = useSuitesTableMetricsModel({
    enableMetricsBatch,
    isAuthenticated,
    metricSeriesBySuiteIdOverride,
    metricsBatchDelayMs,
    metricsEndDay,
    metricsStartDay,
    selectedOrganisationId,
    sparklineRangeDays,
    visibleRows,
  });
  const gridConfig = suitesGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey === undefined ? 'suites-table' : persistKey,
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    includeDeltas: showDeltas,
  });
  const suitesQueryKey = queryKey ?? gridConfig.queryKey;
  const columns = useMemo(
    () =>
      buildSuitesColumns({
        selectedProjectId,
        groupErroredWithFailed,
        preferFileNameOverDisplayName,
        resolveGroupErroredWithFailed,
        resolvePreferFileNameOverDisplayName,
        onProjectSelect: setSelectedProjectId,
        onStatusSelect,
        thresholds: thresholds.suites,
        resolveThresholds,
        showDeltas,
        disableLinks,
        disableSummaryLinks,
        latestAsMeta: true,
        includeTagsInNameColumn: true,
        includeActionsColumn: true,
        hideActionsColumn,
        includeLatestTestsColumn: true,
        includeLastExecutedColumn: false,
        metricSeriesBySuiteId,
        onEditRow: handleEditRow,
        onDeleteRow,
        deleteRowPendingId,
        tagColorByName: resolvedTagColorByName,
      }),
    [
      disableLinks,
      disableSummaryLinks,
      deleteRowPendingId,
      handleEditRow,
      hideActionsColumn,
      onDeleteRow,
      onStatusSelect,
      groupErroredWithFailed,
      preferFileNameOverDisplayName,
      resolveGroupErroredWithFailed,
      resolvePreferFileNameOverDisplayName,
      resolveThresholds,
      metricSeriesBySuiteId,
      resolvedTagColorByName,
      selectedProjectId,
      suitesQueryKey,
      setSelectedProjectId,
      showDeltas,
      thresholds.suites,
    ],
  );
  const togglableColumnFields = useMemo(() => {
    const helperOnlyFields = new Set(['latestRunTimestamp']);
    return columns.map((column) => column.field).filter((field) => !helperOnlyFields.has(field));
  }, [columns]);
  const handleRowsChange = useCallback(
    (rows: SuiteListItemDto[]) => {
      setVisibleRows(rows);
      onRowsChange?.(rows);
    },
    [onRowsChange],
  );

  return {
    columns,
    deleteLoading,
    deleteTarget,
    editEditor,
    editErrors,
    editForm,
    editTarget,
    gridConfig,
    handleEditClose,
    handleEditSave,
    handleRowsChange,
    isAuthenticated,
    onDeleteClose,
    onDeleteConfirm,
    queryFn: queryFn ?? gridConfig.queryFn,
    queryKey: suitesQueryKey,
    selectedOrganisationId,
    selectedProjectId,
    togglableColumnFields,
  };
};
